import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from "@angular/core";
import { forkJoin, Subject } from "rxjs";
import { EngubatorEngineersService } from "../../services/engubator/engubator.engineers.service";
import { EngubatorPlanningService } from "../../services/engubator/engubator.planning.service";
import { KeyValueObject } from "../../models/core/KeyValueObject";
import { ContragentsService } from "../../../categories/contragents/contragents.service";
import { RequestType, RoleEnum } from "../../enums";
import { UserGroupsEnum } from "../../enums/user-groups-enum";
import { EngubatorGetDayStatusModel } from "../../models/engubator/engubator-get-day-status.model";
import { UsersService } from "../../../admin/users/users.service";
import { AppService } from "../../../app.service";
import { PlanningService } from "../../services/planning.service";
import { PlanningDayStatusRequest } from "../../models/planning/planning-day-status-request";
import { NotificationService } from "../../../core/services/notification.service";
import { NotificationType } from "../../../core/services/notification-type";
import { AlgoritmType } from "../../../constants/planning.constants";
import { SecurityService } from "../../../core/services/security.service";
import { TransferInitiatorEnum } from "../../enums/transfer-initiator.enum";

interface IPlanningDayStatus {
    performerIntegraCode: string;
    loading: boolean;
    available: boolean;
    time: string;
    planningTaskId: string;
    isSuccessful: boolean;
    errorDescription: string;
}

interface IPlanningDay {
    id: number;
    dayOfWeekName: string;
    valueName: string;
    value: Date;
    isLastOfWeek: boolean;
    statuses: IPlanningDayStatus[];
    canAssignRedDay: boolean;
}

export class PlannedDayContext {
    visitDate: Date;
    performerId: number;
    performerIntegraCode: string;
    removeActivityTracking: boolean;
    algoritmType: string;
    transferInitiatorId?: number;
}

declare var kendo: any;

@Component({
    selector: 'engineer-visit-planning',
    templateUrl: './engineer-visit-planning.component.html',
    styleUrls: ['./engineer-visit-planning.component.scss',
        '../../../../vendor/libs/angular2-ladda/angular2-ladda.scss',
        '../../../../vendor/libs/ngx-perfect-scrollbar/ngx-perfect-scrollbar.scss',
    ],
    encapsulation: ViewEncapsulation.None,
})
export class EngineerVisitPlanningComponent implements OnInit, OnDestroy {

    @Input() requestId: number;
    @Input() requestIntegraCode: string;
    @Input() continueButtonName: string = 'Оформить визит';
    @Input() performerIntegraCode: string;
    @Input() visitDate: Date;
    @Input() requestServiceCenterId: number;
    @Input() requestServiceCenterIntegraCode: string;
    @Input() disablePerformerSelection: boolean = false;

    @Output() onCancelEvent: EventEmitter<any> = new EventEmitter<any>();
    @Output() onContinueEvent: EventEmitter<PlannedDayContext> = new EventEmitter<PlannedDayContext>();

    private _destroy$ = new Subject<boolean>();
    private uniqueDayId: number = 1;

    defaultDaysRange: number = 7;
    removeActivityTracking: boolean = false;
    transferInitiatorId: number = null;
    initialVisitDate: Date = null;
    readonly transferInitiatorEnum = TransferInitiatorEnum;

    constructor(
        private engubatorEngineersService: EngubatorEngineersService,
        private engubatorPlanningService: EngubatorPlanningService,
        private planningService: PlanningService,
        private contragentsService: ContragentsService,
        private usersService: UsersService,
        private appService: AppService,
        private notificationService: NotificationService
    ) { }

    availablePerformersDropdownData: KeyValueObject[] = [];
    allPlanningDays: IPlanningDay[] = [];
    weekPlanningDays: IPlanningDay[] = [];
    disabledPerformersDropdown: boolean = false;
    disabledTimeSelection: boolean = false;
    dataSaving: boolean = false;

    weekStartDate: Date;
    weekEndDate: Date;
    selectedDayId: number;
    assignedPerformerUserIntegraCode: string;
    time: string;
    timeControlValue: Date;
    algoritmType: string;

    ngOnInit(): void {
        this.initialVisitDate = this.visitDate != null ? new Date(this.visitDate) : null;

        if (this.visitDate == null) {
            this.weekStartDate = this.getCurrentDateOffset(1);
            this.weekEndDate = this.getCurrentDateOffset(this.defaultDaysRange + 1);
        }
        else {
            this.weekStartDate = this.visitDate;
            this.weekEndDate = new Date(this.visitDate);
            this.weekEndDate.setDate(this.weekEndDate.getDate() + this.defaultDaysRange);
            this.time = `${this.visitDate.getHours()}:${this.visitDate.getMinutes()}`;
            this.timeControlValue = new Date(this.visitDate);
        }

        const assignedPerformerUser$ = this.contragentsService.getContragentAssignedPerformerUser(this.requestId, <number>UserGroupsEnum.install);
        const availablePerformers$ = this.engubatorEngineersService.getAvailablePerformers(this.requestIntegraCode, [<number>RoleEnum.master, <number>RoleEnum.specialist], this.requestServiceCenterIntegraCode);

        forkJoin([assignedPerformerUser$, availablePerformers$])
            .subscribe(([assignedPerformerUser, availablePerformers]) => {
                this.assignedPerformerUserIntegraCode = assignedPerformerUser != null && this.visitDate == null
                    ? assignedPerformerUser.integraCode
                    : null;

                this.availablePerformersDropdownData = availablePerformers.data.map(x => {
                    let item = new KeyValueObject(x.userId, x.fullName);
                    item.integraCode = x.integraCode;
                    return item;
                });

                if (this.assignedPerformerUserIntegraCode != null && this.performerIntegraCode == null) {
                    this.performerIntegraCode = this.assignedPerformerUserIntegraCode;
                }

                if (this.disablePerformerSelection) {
                    if (this.assignedPerformerUserIntegraCode != null) {
                        this.performerIntegraCode = this.assignedPerformerUserIntegraCode;
                    }

                    this.disabledPerformersDropdown = true;
                    this.disabledTimeSelection = true;
                }

                if (!this.availablePerformersDropdownData.map(m => m.integraCode).includes(this.performerIntegraCode)) {
                    this.performerIntegraCode = null;
                }
            });

        this.generateDays(this.weekStartDate);
        this.checkAlreadySelectedDay();
        this.initWeekDays();

        if (this.visitDate != null) {
            let day = this.allPlanningDays.find(x => x.value.getDate() == this.visitDate.getDate());
            day.statuses.push({
                performerIntegraCode: this.performerIntegraCode,
                available: true,
                time: this.time,
                loading: false
            } as IPlanningDayStatus);
            this.selectedDayId = day.id;
        }
    }

    changeAlgoritmType(day: IPlanningDay, algoritmType: string): void {
        this.algoritmType = algoritmType;
        day.canAssignRedDay = algoritmType === AlgoritmType.manual;
    }

    ngOnDestroy(): void {
        this._destroy$.next(true);
        this._destroy$.complete();
    }

    saveChanges() {
        this.dataSaving = true;
        let selectedDay = this.allPlanningDays.find(x => x.id == this.selectedDayId);
        let selectedDayStatus = this.getCurrentDayStatus(selectedDay);

        const confirmFunc = () => {
            this.engubatorPlanningService.confirmPlannedVisit(this.requestIntegraCode, selectedDayStatus.planningTaskId).subscribe(() => {
                if (this.performerIntegraCode != null) {
                    this.usersService.getUserIdByIntegraCode(this.performerIntegraCode).subscribe((resp) => {
                        this.saveChangesCore(resp, this.performerIntegraCode);
                    });
                }
                else {
                    this.saveChangesCore();
                }
            })
        };

        if (!selectedDayStatus.isSuccessful) {
            this.notificationService.error({
                title: 'Ошибка',
                message: selectedDayStatus.errorDescription,
                confirmButtonText: 'Ок',
                notificationType: NotificationType.SweetAlert
            });
            this.dataSaving = false;
        } else if (!selectedDayStatus.available) {
            this.notificationService.confirmation({
                title: 'Предупреждение',
                message: 'Вы уверены, что хотите выбрать этот день для планирования?',
                showCancelButton: true,
                showConfirmButton: true,
                cancelButtonText: 'Отменить',
                confirmButtonText: 'Подтвердить'
            }, () => { confirmFunc(); }, () => { this.dataSaving = false; });
        } else {
            confirmFunc();
        }
    }

    saveChangesCore(performerUserId: number = null, performerIntegraCode: string = null) {
        let plannedContext = {
            visitDate: this.getCompleteDate(),
            performerId: performerUserId,
            performerIntegraCode: performerIntegraCode,
            removeActivityTracking: this.removeActivityTracking,
            algoritmType: this.algoritmType,
            transferInitiatorId: this.isVisitDayChanged ? this.transferInitiatorId : null
        } as PlannedDayContext;

        this.onContinueEvent.emit(plannedContext);
    }

    get isVisitDayChanged(): boolean {
        if (this.initialVisitDate == null || this.selectedDayId == null) {
            return false;
        }

        const selectedDate = this.getCompleteDate();
        return selectedDate.toDateString() !== this.initialVisitDate.toDateString();
    }

    get canSaveChanges(): boolean {
        if (this.selectedDayId == null) {
            return false;
        }

        if (this.isVisitDayChanged && this.transferInitiatorId == null) {
            return false;
        }

        return true;
    }

    getCompleteDate(): Date {
        const [hours, minutes] = this.time != null
            ? this.time.split(':').map(Number)
            : [0, 0];

        let selectedDay = this.allPlanningDays.find(x => x.id == this.selectedDayId);

        let date = new Date(
            selectedDay.value.getFullYear(),
            selectedDay.value.getMonth(),
            selectedDay.value.getDate(),
            hours,
            minutes
        );

        return date;
    }

    cancel() {
        this.onCancelEvent.emit();
    }

    moveToBackWeek() {
        this.moveWeek(this.defaultDaysRange * (-1));
    }

    moveToNextWeek() {
        this.moveWeek(this.defaultDaysRange);
    }

    moveWeek(daysRange: number) {
        this.weekStartDate.setDate(this.weekStartDate.getDate() + daysRange);
        this.weekEndDate.setDate(this.weekEndDate.getDate() + daysRange);

        if (!this.allPlanningDays.some(x => x.value >= this.weekStartDate && x.value < this.weekEndDate)) {
            this.generateDays(this.weekStartDate);
        }

        this.initWeekDays();
    }

    initWeekDays() {
        this.weekPlanningDays = this.allPlanningDays.filter(x => x.value >= this.weekStartDate && x.value < this.weekEndDate);
    }

    private generateDays(startDate: Date = new Date()) {
        const startDay = new Date(startDate);

        for (let i = 0; i < this.defaultDaysRange; i++) {
            const date = new Date(startDay);
            date.setDate(startDay.getDate() + i);

            const dayOfWeek = date.getDay();

            this.allPlanningDays.push({
                id: this.uniqueDayId,
                value: date,
                valueName: `${date.getDate()} ${this.appService.getMonthName(date, false)}`,
                dayOfWeekName: this.appService.getDayOfWeekName(date),
                isLastOfWeek: i == this.defaultDaysRange - 1,
                statuses: [],
                canAssignRedDay: false
            });

            this.uniqueDayId += 1;
        }
    }

    private checkAlreadySelectedDay() {
        if (this.visitDate != null && this.allPlanningDays.some(x => x.value == this.visitDate)) {
            let selectedDay = this.allPlanningDays.find(x => x.value == this.visitDate);
            selectedDay.statuses.push({
                available: true,
                loading: false,
                planningTaskId: null,
                time: null,
                performerIntegraCode: this.performerIntegraCode,
                errorDescription: null,
                isSuccessful: true
            });
        }
    }

    private getCurrentDateOffset(offset: number): Date {
        let startDay = new Date();
        startDay.setDate(startDay.getDate() + offset);

        return startDay;
    }

    getCurrentMonthAndYear() {
        let minDay = this.weekPlanningDays.reduce((min, current) => current.value < min.value ? current : min);
        return minDay != null ? `${this.appService.getMonthName(minDay.value)} ${minDay.value.getFullYear()}` : '';
    }

    getWeekStartDayName(): string {
        let minDay = this.weekPlanningDays.reduce((min, current) => current.value < min.value ? current : min);
        return minDay != null ? minDay.valueName : '';
    }

    getWeekEndDayName(): string {
        let maxDay = this.weekPlanningDays.reduce((max, current) => current.value > max.value ? current : max);
        return maxDay != null ? maxDay.valueName : '';
    }

    timeChange(event: Date) {
        this.selectedDayId = null;
        this.time = event != null
            ? `${event.getHours()}:${event.getMinutes()}`
            : null;
    }

    performerChange(event: any) {
        this.selectedDayId = null;
    }

    selectDay(day: IPlanningDay) {
        let hasAvailableDayStatus = false;

        if (day.canAssignRedDay) {
            hasAvailableDayStatus = day.statuses.some(x => x.performerIntegraCode == this.performerIntegraCode && x.time == this.time);
        } else {
            hasAvailableDayStatus = day.statuses.some(x => x.performerIntegraCode == this.performerIntegraCode && x.time == this.time && x.available);
        }

        if (hasAvailableDayStatus) {
            this.selectedDayId = this.selectedDayId == null || this.selectedDayId != day.id ? day.id : null;
        }
        else if (!this.dayHasStatus(day)) {
            this.toggleDayStatusBar(day);
        }
    }

    toggleDayStatusBar(day: IPlanningDay) {
        this.planningService.getAlgoritmType().subscribe(res => {
            if (res) {
                this.changeAlgoritmType(day, res);
            }

            let status = {
                performerIntegraCode: this.performerIntegraCode,
                loading: true,
                available: false,
                time: this.time,
                isSuccessful: true,
                errorDescription: null
            } as IPlanningDayStatus;

            let dayStatusModel = {
                requestId: this.requestId,
                requestIntegraCode: this.requestIntegraCode,
                performerIntegraCode: this.performerIntegraCode,
                visitDate: day.value,
                time: this.time,
                serviceCenterId: this.requestServiceCenterId,
                algoritmType: this.algoritmType,
            } as PlanningDayStatusRequest;

            let manualAction = () => {
                this.changeAlgoritmType(day, AlgoritmType.manual);
                let cancelCheckDayStatusManualAction$ = new Subject<void>();
                dayStatusModel.algoritmType = this.algoritmType;
                this.planningService.checkDayStatus(dayStatusModel, true, cancelCheckDayStatusManualAction$).subscribe(respAlt => {
                    status.loading = false;

                    if (respAlt.isSuccessful) {
                        status.available = respAlt.data.dayAvailable;
                        status.planningTaskId = respAlt.data.planningTaskId;
                        status.isSuccessful = respAlt.data.isSuccessful;
                        status.errorDescription = respAlt.data.errorDescription;
                        this.removeActivityTracking = true;
                        this.notifyIfDayUnavailable(respAlt.data);

                        if (status.available == false && this.disabledPerformersDropdown == true && this.disablePerformerSelection == false) {
                            this.disabledPerformersDropdown = false;
                        }
                    } else {
                        status.available = false;
                        status.isSuccessful = false;
                        status.errorDescription = respAlt.data.errorDescription;
                    }
                }, (err) => {
                    status.loading = false;
                    status.available = false;
                    status.isSuccessful = false;
                    status.errorDescription = err;
                    cancelCheckDayStatusManualAction$.next();
                });
            };

            let cancelCheckDayStatus$ = new Subject<void>();
            this.planningService.checkDayStatus(dayStatusModel, this.algoritmType === AlgoritmType.manual, cancelCheckDayStatus$).subscribe(resp => {
                status.loading = false;

                if (resp.isSuccessful) {
                    status.available = resp.data.dayAvailable;
                    status.planningTaskId = resp.data.planningTaskId;
                    status.isSuccessful = resp.data.isSuccessful;
                    status.errorDescription = resp.data.errorDescription;
                    this.notifyIfDayUnavailable(resp.data);

                    if (status.available == false && this.disabledPerformersDropdown == true && this.disablePerformerSelection == false) {
                        this.disabledPerformersDropdown = false;
                    }

                } else if (this.algoritmType === AlgoritmType.yandex) {
                    this.notificationService.error({
                        title: 'Ошибка',
                        message: 'Произошел сбой в сервисе маршрутизации. Переключаем на мануальный режим планирования',
                        notificationType: NotificationType.SweetAlert
                    }, manualAction, manualAction, manualAction);
                }
            }, () => {
                this.notificationService.error({
                    title: 'Ошибка',
                    message: 'Произошел сбой в сервисе маршрутизации. Переключаем на мануальный режим планирования',
                    notificationType: NotificationType.SweetAlert
                }, manualAction, manualAction, manualAction);
                cancelCheckDayStatus$.next();
            });

            day.statuses.push(status);
        });
    }

    dayIsCompletly(day: IPlanningDay): boolean {
        let status = this.getCurrentDayStatus(day);
        return status != null && status.loading == false;
    }

    dayHasStatus(day: IPlanningDay): boolean {
        let status = day.statuses.length > 0 && day.statuses.some(x => x.performerIntegraCode == this.performerIntegraCode && x.time == this.time);
        return status;
    }

    getDayStatusClass(day: IPlanningDay): string {
        let status = this.getCurrentDayStatus(day);
        let className = status.loading ? '' : status.available ? 'green-day' : 'red-day';

        if (day.id == this.selectedDayId) {
            if (day.canAssignRedDay) {
                className += status.available ? ' selected-green-day' : ' selected-red-day'
            } else {
                className += ' selected-green-day';
            }
        }

        return className;
    }

    dayIsLoading(day: IPlanningDay): boolean {
        let loadingStatus = this.dayHasStatus(day)
            ? this.getCurrentDayStatus(day).loading
            : false;

        return loadingStatus
    }

    private notifyIfDayUnavailable(data: { dayAvailable: boolean; isSuccessful: boolean; unavailabilityReason: string }): void {
        if (!data || data.isSuccessful === false || data.dayAvailable !== false || !data.unavailabilityReason) {
            return;
        }

        this.notificationService.warning({
            title: 'Планирование недоступно',
            message: data.unavailabilityReason,
            notificationType: NotificationType.Toast
        });
    }

    private getCurrentDayStatus(day: IPlanningDay): IPlanningDayStatus {
        return day.statuses.find(x => x.performerIntegraCode == this.performerIntegraCode && x.time == this.time);
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.cancel();
        }
    }
}